import React, { useEffect, useRef, useState,useMemo} from 'react';
import styles from './EmergencyAgsnConflict.module.css';
import { useHistory } from 'react-router-dom';
import { BackButton, EmergencyAgsnToolbar } from '..';
import Table from '../../components/Table/Table';
import {
  API_RESOURCE_URLS,
  DATE_FORMAT,
  DISPLAY_MESSAGES,
  MESSAGE_TYPE,
  RESOURCE_TYPE,
  USER_OPERATIONS,
} from '../../constants';
import { withAllowedOperationsProvider } from '../../hocs';
import PropTypes from 'prop-types';
import moment from 'moment';
import { API } from '../../apis/api';
import { usePlantAndLinesStore } from '../../providers/PlantAndLinesStore/PlantAndLinesStore';
import { usePopupManager } from '../../providers/PopupManager/PopupManager';
import { buildErrorMessage } from '../../apis/calls';
import { downloadCsv } from '../../utils/utils';

const EmergencyAgsnConflict = ({
  hasActiveEdits,
  setHasActiveEdits,
  aggregate,
  rowsDataForEAgsnConflict,
  lastRefreshedDate,
  loadPlannedEmergencyAgsn,
  isEAgnEdit,
  setEAgsnEdit,
}) => {
  const history = useHistory();
  const ROW_HEIGHT = 38;

  const [plant] = usePlantAndLinesStore().plantState;
  const [line] = usePlantAndLinesStore().lineState;
  const {
    showPopup,
    showInternalError,
    showLoading,
    closePopUp,
  } = usePopupManager();

  const [selectAllOption, setSelectAllOption] = useState(null);
  const [editConflictValues, setEditConflictValues] = useState([]);
  const radioButtonsRefs = useRef([]);

  const fixedColumns = [
    {
      width: 140,
      title: 'ASN',
      field: 'parentAsn',
      enableSearch: true,
      enableFilter: true,
    },
    {
      width: 170,
      title: 'Part NO.',
      field: 'materialNumber',
      enableSearch: true,
      enableFilter: true,
    },
    {
      width: 200,
      title: 'Part Description',
      field: 'materialDescription',
      enableSearch: true,
      enableFilter: true,
    },
    {
      width: 200,
      title: 'Requirement From',
      field: 'parentLineName',
      enableSearch: true,
      enableFilter: true,
    },
    {
      width: 230,
      title: 'Chassis Planned Date',
      field: 'parentPlannedProductionDate',
      enableSearch: true,
      enableFilter: true,
      render: row => {
        return moment(row.parentPlannedProductionDate).format(
          DATE_FORMAT.DD_MM_YYYY,
        );
      },
    },
    {
      width: 240,
      title: (
        <div>
          <div >Fulfillment From</div>
          <div >
            {[...new Set((rowsDataForEAgsnConflict.data || []).flatMap(row =>
              (row.candidateLines || []).map(line => line.lineName)
            ))].sort().map(option => (
              <label key={option}>
                <input
                className={styles.providedByRadioButton}
                  type="radio"
                  name="selectAllFulfillment"
                  checked={selectAllOption === option}
                  onChange={() => handleSelectAll(option)}
                />
                {option}
              </label>
            ))}
          </div>
        </div>
      ),
field: 'Provided By',
      render: function (row, index) {
        const rowIndex = index - 1;
        return (
          <div className={styles.providedByRadioButtonContainer}>
            {row.candidateLines
              .sort((a, b) => (a.lineName > b.lineName ? 1 : -1))
              .map((candidateLine, candidateLineIndex) => (
                <div className={styles.providedByRadioButton}>
                  <input
                    ref={
                      radioButtonsRefs.current[
                        2 * rowIndex + candidateLineIndex
                      ]
                    }
                    id={`provideBy${rowIndex}`}
                    value={candidateLine.lineId}
                    name={`provideBy${rowIndex}`}
                    type="radio"
                    checked={
                        selectAllOption
                          ? candidateLine.lineName === selectAllOption: !!editConflictValues.find(
                              item =>
                                item.materialId === row.id &&
                                item.lineId === candidateLine.lineId
                            )
                      }
                    disabled={selectAllOption && candidateLine.lineName !== selectAllOption}
                    onChange={e => handleEdit(e, row.id)}
                    data-testid={`provideBy-${row.id}`}
                  />
                  <span className={styles.provideByLabel}>
                    {candidateLine.lineName}
                  </span>
                </div>
              ))}
          </div>
        );
      },
    },
  ];

  const redirectToApplicationLandingPage = () => {
    history.push('/');
  };

  const handleEdit = (e, childMaterialId) => {
    setEAgsnEdit(true);
    setEditConflictValues(prevState => {
      let conflictData = [...prevState];
      const alreadyPresentItemIndex = prevState.findIndex(
        item => item.materialId === childMaterialId,
      );
      if (alreadyPresentItemIndex !== -1)
        conflictData[alreadyPresentItemIndex] = {
          lineId: e.target.value,
          materialId: childMaterialId,
        };
      else
        conflictData = [
          ...conflictData,
          { lineId: e.target.value, materialId: childMaterialId },
        ];
      if (selectAllOption) setSelectAllOption(null);
      return conflictData;
    });
  };

  const resetRadioButtons = () => {
    radioButtonsRefs.current.forEach(radioButton => {
      if (radioButton.current) radioButton.current.checked = false;
    });
    setSelectAllOption(null);
    setEditConflictValues([]);
    setEAgsnEdit(false);
  };

  const resolveConflict = async () => {
    try {
      showLoading();
      const visibleRows = getVisibleRows();
      const visibleIds = new Set(visibleRows.map(r => r.id));
      const filteredValues = editConflictValues.filter(i => visibleIds.has(i.materialId));

      const response = await API.patch(
        API_RESOURCE_URLS.resolveEmergencyAgsnConflicts(plant.value),
        filteredValues
      );

      if (response) {
        resetRadioButtons();
        await loadPlannedEmergencyAgsn();
        closePopUp();
        showPopup({
          type: MESSAGE_TYPE.SUCCESS,
          contextText: DISPLAY_MESSAGES.RESOLVE_CONFLICT_SUCCESS,
        });
        setEAgsnEdit(false);
        setEditConflictValues([]);
      }
    } catch (error) {
      console.error('Resolve conflict failed.' + buildErrorMessage(error));
      showInternalError({
        contextText: DISPLAY_MESSAGES.RESOLVE_CONFLICT_FAILED,
        error,
      });
    }
  };

  const getVisibleRows = () =>
    rowsDataForEAgsnConflict?.filteredData?.length
      ? rowsDataForEAgsnConflict.filteredData
      : rowsDataForEAgsnConflict?.data || [];

  const handleSelectAll = option => {
    const visibleRows = getVisibleRows();
    if (selectAllOption === option) {
      resetRadioButtons();
    } else {
      setSelectAllOption(option);

      const newSelections = visibleRows.flatMap(row =>
        (row.candidateLines || [])
          .filter(line => line.lineName === option)
          .map(line => ({ lineId: line.lineId, materialId: row.id }))
      );

      setEditConflictValues(newSelections);
      setEAgsnEdit(newSelections.length > 0);
    }
  };

  const prepareJsonHeaderAndValueFormats = aggregatePartNames => {
    const vcAggregateHeaderAndValueFormats = aggregatePartNames.map(
      partName => ({
        label: partName + ' Status',
        value: row =>
          row.materialGroupClusters.filter(group => group.name === partName)[0]
            ?.materials[0]?.aggregateStatus || 'unavailable',
      }),
    );

    const vcHeaderAndValueFormats = [
      {
        label: 'ASN',
        value: row => row.parentAsn,
      },
      {
        label: 'Part Number',
        value: row => '="' + row.materialNumber + '"',
      },
      {
        label: 'Part Description',
        value: row => row.materialDescription,
      },
      {
        label: 'Requirement From',
        value: row => row.parentLineName,
      },
      {
        label: 'Chassis Planned Date',
        value: row => moment(row.parentPlannedProductionDate).format('DD MMM YYYY'),
      },
      {
        label: 'Fulfillment From',
        value: row => row.lineName,
      }, 
    ].filter(c => c);
    return [...vcHeaderAndValueFormats, ...vcAggregateHeaderAndValueFormats];
  };

  const generateAndDownloadLineoutCsvReport = filteredData => {
    try {
      if (!rowsDataForEAgsnConflict.loading) {
        const dateValue = moment().format(DATE_FORMAT.ISO);
        
        const jsonData = filteredData;

        const aggregatePartNames = [];

        const { parseAsync } = require('json2csv');
        const fields = prepareJsonHeaderAndValueFormats(aggregatePartNames);
        const opts = { fields };
        parseAsync(jsonData, opts)
          .then(csv => {
            const exportedFilename =
              'EAgsnConflictReport_' +
              plant.label +
              '_' +
              line.label +
              '_' +
              dateValue +
              '.csv';
            downloadCsv(csv, exportedFilename);
          })
          .catch(error =>
            console.error(
              'Failed to download eAgsn conflict report, error:' +
                buildErrorMessage(error),
            ),
          );
      }
    } catch (error) {
      console.error(
        'Failed to download eAgsn Conflict report, error:' + buildErrorMessage(error),
      );
    }
  };

  const computedHeaderHeight = useMemo(() => {
  const visibleRows = getVisibleRows();
  const maxCandidateCount = visibleRows?.length
    ? Math.max(...visibleRows.map(row => (row.candidateLines || []).length))
    : 1;
 
  // Adjust base and per-row increase as needed
  const BASE_HEADER_HEIGHT = 38; 
  const EXTRA_PER_LINE = 18;
 
  return BASE_HEADER_HEIGHT + (maxCandidateCount - 1) * EXTRA_PER_LINE;
}, [rowsDataForEAgsnConflict.filteredData, rowsDataForEAgsnConflict.data]);

  useEffect(() => {
    setHasActiveEdits(isEAgnEdit);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEAgnEdit]);

  useEffect(() => {
    radioButtonsRefs.current = rowsDataForEAgsnConflict.filteredData?.flatMap(
      (row, rowIndex) =>
        row.candidateLines.map(
          (_, candidateLineIndex) =>
            (radioButtonsRefs.current[
              rowIndex + candidateLineIndex
            ] = React.createRef()),
        ),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!selectAllOption) return;
    const visibleRows = getVisibleRows();
    const recomputed = visibleRows.flatMap(row =>
      (row.candidateLines || [])
        .filter(line => line.lineName === selectAllOption)
        .map(line => ({ lineId: line.lineId, materialId: row.id }))
    );
    setEditConflictValues(recomputed);
    setEAgsnEdit(recomputed.length > 0);
  }, [rowsDataForEAgsnConflict.filteredData, rowsDataForEAgsnConflict.data, selectAllOption]);

  useEffect(() => {
    const visibleRows = getVisibleRows();
    radioButtonsRefs.current = visibleRows.flatMap(() =>
      (visibleRows.candidateLines || []).map(() => React.createRef())
    );
  }, [rowsDataForEAgsnConflict.filteredData, rowsDataForEAgsnConflict.data]);

  return (
    <>
      <div className={styles.backButton}>
        <BackButton
          basePath={aggregate}
          action="Emergency Agsn"
          handleClick={redirectToApplicationLandingPage}
        />
      </div>
      <EmergencyAgsnToolbar
        lastRefreshedDate={lastRefreshedDate}
        hasActiveEdits={hasActiveEdits}
        restrictNavigationDuringEditing
      />
      <div className={styles.tableView}>
        <Table
          columns={[...fixedColumns]}
          defaultColumns={[...fixedColumns]}
          rowHeight={ROW_HEIGHT}
          headerHeight={computedHeaderHeight}
          rows={rowsDataForEAgsnConflict.data}
          setFilteredDataInParent={filtered => {
            rowsDataForEAgsnConflict.filteredData = filtered;
          }}
          setAsnCount={() => {}}
          isDataLoading={rowsDataForEAgsnConflict.loading}
          hideCheckbox
          useDynamicRowHeight
          primaryAction={{
            name: 'RESOLVE CONFLICT',
            actionFn: () => resolveConflict(),
            shouldEnable: () => isEAgnEdit,
            authOperation: USER_OPERATIONS.ASSIGN_MATERIAL_TO_LINE,
          }}
          secondaryActions={[
            {
              name: 'DOWNLOAD CSV',
              getFilteredData: true,
              authOperation: USER_OPERATIONS.GET_CHASSIS_PLAN_ASN,
              shouldEnable: () => rowsDataForEAgsnConflict.data.length > 0,
              actionFn: filteredData =>
                generateAndDownloadLineoutCsvReport(filteredData),
            },
          ]}
        />
      </div>
    </>
  );
};
EmergencyAgsnConflict.propTypes = {
  hasActiveEdits: PropTypes.bool.isRequired,
  setHasActiveEdits: PropTypes.func.isRequired,
  aggregate: PropTypes.string.isRequired,
  lastRefreshedDate: PropTypes.objectOf(moment),
  rowsDataForEAgsnConflict: PropTypes.arrayOf(
    PropTypes.shape({
      loading: PropTypes.bool.isRequired,
      data: PropTypes.array.isRequired,
      filteredData: PropTypes.array.isRequired,
    }),
  ),
  setEAgsnEdit: PropTypes.func.isRequired,
  loadPlannedEmergencyAgsn: PropTypes.func.isRequired,
};
export default withAllowedOperationsProvider(
  EmergencyAgsnConflict,
  true,
  RESOURCE_TYPE.MATERIAL,
);
